import React, { useEffect, useState } from 'react';
import { Card, Container, Row, Button, Alert } from 'react-bootstrap';
import { get, isEmpty } from 'lodash';
import DynamicForm from 'screens/common/dynamicForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faDownload,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { useNavigate } from 'react-router-dom';
import { farmerReportApi } from 'api/endpoints/farmers';
import { pulaFarmlandApi } from 'api/endpoints/PulaFarmland';
import { formSubmissionToast } from 'helpers/utils';
import { getTenantFromToken } from 'utils/routeUtils';
import useOnlineStatus from 'hooks/useOnlineStatus';
import offlineDBService from 'db/dexieDB';
import { toast } from 'react-toastify';

function CreateFarmer(props) {
  const tenant = getTenantFromToken();
  const tns_tenant = '03534add672340809e2c70314b1ce20e';
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(null);
  const [offlineMode, setOfflineMode] = useState(false);

  // Get online status from the hook
  const isOnline = useOnlineStatus();

  // Fetch form data - tries API first, falls back to local DB if offline
  const fetchFormData = async () => {
    try {
      if (isOnline) {
        // Online - fetch from API
        const formResponse = await farmerReportApi.getDynamicFarmerForm(
          tns_tenant
        );
        setData(formResponse.data.results);
        setOfflineMode(false);
      } else {
        // Offline - fetch from IndexedDB
        console.log('Device is offline, fetching form from local DB');
        const formDef = await offlineDBService.getFormDefinition(
          'farmer-create'
        );

        if (formDef && formDef.formData) {
          console.log('Form found in local DB:', formDef.formData);
          setData(formDef.formData);
          setOfflineMode(true);
        } else {
          console.error('No form definition found in local DB');
          setData({});
          toast.error(
            'No offline form data available. Please download when online.'
          );
        }
      }
    } catch (error) {
      console.error('Error fetching form data:', error);

      // Try to fall back to local DB even if error is not offline-related
      try {
        const formDef = await offlineDBService.getFormDefinition(
          'farmer-create'
        );
        if (formDef && formDef.formData) {
          console.log('Falling back to local DB after API error');
          setData(formDef.formData);
          setOfflineMode(true);
        } else {
          toast.error('Failed to load form. No offline data available.');
        }
      } catch (dbError) {
        console.error('Error fetching from local DB:', dbError);
        toast.error('Failed to load form data');
      }
    }
  };

  // Re-fetch data when online status changes
  useEffect(() => {
    fetchFormData();
  }, [isOnline, tns_tenant]);

  // Handle form submission - improved error handling
  const handleFormSubmit = async formData => {
    try {
      if (isOnline) {
        // Online - submit to API
        try {
          const response = await farmerReportApi.sendFarmerCreate(
            tns_tenant,
            formData
          );
          formSubmissionToast(response, toast, 'Farmer');

          // Async Pula Compliance Check Polling
          if (formData.nin) {
            pollComplianceStatus(formData.nin);
          }

          navigate(`/${tenant}/farmerReport/`);
        } catch (err) {
          console.error('Error submitting form:', err);
          // Show the API error directly without falling back to local storage
          formSubmissionToast(err, toast, 'Farmer');
          return;
        }
      } else {
        // Offline - save to IndexedDB
        console.log('Device is offline, saving form submission locally');
        await offlineDBService.saveFarmer(formData);
        toast.success('Farmer data saved locally. Will sync when online.');
        navigate(`/offline-data/`);
      }
    } catch (err) {
      // This catch block will handle unexpected errors not related to API or offline storage
      console.error('Unexpected error in form submission:', err);

      if (!isOnline) {
        // Only attempt local storage as fallback if offline
        try {
          await offlineDBService.saveFarmer(formData);
          toast.warning('Error occurred. Data saved locally for later sync.');
          navigate(`/${tenant}/farmerReport/`);
        } catch (dbError) {
          console.error('Error saving locally:', dbError);
          toast.error('Failed to save data locally. Please try again.');
        }
      } else {
        // If online and we reached this point, it's a truly unexpected error
        toast.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  // Handle compliance status polling
  const pollComplianceStatus = async (nin, maxAttempts = 5) => {
    let attempts = 0;
    const interval = 3000; // 3 seconds

    const poll = async () => {
      try {
        const res = await pulaFarmlandApi.getComplianceStatus(nin);
        const status = res.results?.status;
        console.log(`DEBUG: Compliance status for ${nin}:`, status);

        if (status === 'MISSING') {
          toast.warning(
            'Note: This NIN does not contain farmland details in Pula.',
            {
              autoClose: 10000,
              position: 'top-right'
            }
          );
          return true; // Stop polling
        } else if (status === 'FOUND') {
          console.log('Farmland details found in Pula');
          return true; // Stop polling
        }
      } catch (err) {
        console.error('Error polling compliance status:', err);
      }
      return false; // Continue polling
    };

    const runPolling = async () => {
      while (attempts < maxAttempts) {
        const stop = await poll();
        if (stop) break;
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      }
    };

    runPolling();
  };

  // Handle download button click
  const header = (
    <div className="d-flex align-items-center">
      <div
        className="me-2 my-auto theme-control-toggle"
        onClick={() =>
          offlineMode
            ? navigate(`/offline-data`)
            : navigate(`/${tenant}/farmerReport`)
        }
      >
        <div className="theme-control-toggle-label">
          <FontAwesomeIcon icon={faArrowLeft} className="fs-0" />
        </div>
      </div>
      <span>Create Farmer Details</span>
      {offlineMode && (
        <span className="ms-2 badge bg-warning">Offline Mode</span>
      )}
    </div>
  );

  return (
    <Container>
      {/* <Row className="mb-3">
        <Card>
          <Card.Body className="d-flex justify-content-between align-items-center">
            <div>
              {isOnline ? (
                <span className="text-success">
                  <FontAwesomeIcon icon={faSync} className="me-1" />
                  Online
                </span>
              ) : (
                <span className="text-warning">
                  <FontAwesomeIcon icon={faSync} className="me-1" spin />
                  Offline
                </span>
              )}
            </div>

            <Button variant="primary" disabled={!isOnline || isDownloading}>
              <FontAwesomeIcon icon={faDownload} className="me-1" />
              {isDownloading ? 'Downloading...' : 'Download for Offline Use'}
            </Button>
          </Card.Body>
        </Card>
      </Row> */}

      {downloadSuccess === true && (
        <Row className="mb-3">
          <Alert variant="success">
            Data downloaded successfully. You can now use the app offline.
          </Alert>
        </Row>
      )}

      {downloadSuccess === false && (
        <Row className="mb-3">
          <Alert variant="danger">
            Failed to download data. Please try again later.
          </Alert>
        </Row>
      )}

      {!isOnline && isEmpty(data) && (
        <Row className="mb-3">
          <Alert variant="warning">
            You are offline and no form data is available. Please connect to the
            internet and download data for offline use.
          </Alert>
        </Row>
      )}

      <Row>
        <Card className="mb-3">
          <Card.Header>
            <FalconCardHeader
              title={header}
              className="ps-lg-4"
              titleTag="h5"
              titleClass="mb-0"
              light
            />
          </Card.Header>
          <Card.Body>
            {!isEmpty(data) && (
              <DynamicForm
                data={data}
                header={true}
                onSubmit={handleFormSubmit}
              />
            )}
          </Card.Body>
        </Card>
      </Row>
    </Container>
  );
}

export default CreateFarmer;
